import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { type Message } from "@lr-notitia/notitia-alternis/schemas";
import { api, apiConfig as operatorAPIConfig } from "@lr-notitia/notitia-operator/client";
import { useGraphStore } from "../graph";
import { useDomainStore } from "../domain";
import { useSymbolsStore } from "../symbols";
import { useConfigStore } from "../alternis/config";
import { useInstanceWorkspacesStore } from "../i-workspace";
import { useInstanceDataspacesStore } from "../i-dataspace";
import { Question } from "../questions";
import { useAlternisNewStore } from "../alternis_new";

type ContentEnum = {
	name: string;
	description: string;
	id: string;
	synonyms?: string[];
};
type ExpectedResponse = "text generation" | "enum.id" | "number" | "date" | "time" | "timestamp";
type ContentAttribute = {
	name: string;
	description: string;
	id: string;
	expectedResponse: ExpectedResponse;
	isArray?: boolean;
	enums?: ContentEnum[];
};
type Content = {
	chat: string[];
	question_to_answer: string;
	attributes: ContentAttribute[];
};
type Suggestions = Record<string, string | string[]>;

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
let configStore: ReturnType<typeof useConfigStore>;
let workspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
let dataspaceStore: ReturnType<typeof useInstanceDataspacesStore>;
let alternisNewStore: ReturnType<typeof useAlternisNewStore>;

function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	symbolsStore = useSymbolsStore();
	configStore = useConfigStore();
	workspacesStore = useInstanceWorkspacesStore();
	dataspaceStore = useInstanceDataspacesStore();
	alternisNewStore = useAlternisNewStore();
}

export const useGptSuggestionsStore = defineStore("gpt-suggestions", () => {
	const suggestionData = ref<Suggestions>({});
	const currentlyProcessing = ref<number>(0);

	function getLocalISOString(): string {
		const now = new Date();
		const date = now.toLocaleDateString("en-CA", { timeZone: "Europe/Berlin" }); // en-CA formats as YYYY-MM-DD
		const time = now.toLocaleTimeString("en-GB", { timeZone: "Europe/Berlin", hour12: false }); // en-GB formats as HH:MM:SS
		const offset =
			now
				.toLocaleTimeString("en-GB", { timeZone: "Europe/Berlin", timeZoneName: "longOffset" })
				.split(" ")
				.pop()
				?.replace("GMT", "") || "+00:00"; // extracts e.g. "+01:00" from "14:30:00 GMT+01:00"
		return `${date}T${time}${offset}`;
	}

	const longToShort: { [longId: string]: string } = {};
	const shortToLong: { [shortId: string]: string } = {};
	let idCounter = 0;
	function getShortId(longId: string) {
		if (longToShort[longId]) return longToShort[longId];
		const shortId = "ID" + idCounter++;
		longToShort[longId] = shortId;
		shortToLong[shortId] = longId;
		return shortId;
	}
	function getLongId(shortId: string) {
		return shortToLong[shortId];
	}

	function improveGptResponse(gptRes: string): string {
		// remove all characters before the first "{" and after the last "}"
		let start = gptRes.indexOf("{");
		let end = gptRes.lastIndexOf("}");
		if (start === -1 || end === -1) return gptRes;
		return gptRes.slice(start, end + 1);
	}

	// Check that a YYYY-MM-DD triple is a real calendar date (rejects e.g. 2026-13-40, 2026-02-30).
	function isRealDate(year: number, month: number, day: number): boolean {
		const date = new Date(Date.UTC(year, month - 1, day));
		return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
	}

	// Validate a single returned value against the attribute's expected datatype.
	// Returns the normalized value to store (enum short ids resolved to long ids),
	// or null when the value does not match the expected format so the caller can drop it.
	function validateAndNormalize(attr: ContentAttribute, raw: unknown): string | null {
		if (typeof raw !== "string") return null;
		const v = raw.trim();
		if (!v || v === "?") return null;
		switch (attr.expectedResponse) {
			case "number":
				// a single number only — ranges, units or any other text are rejected
				return /^-?\d+(\.\d+)?$/.test(v) ? v : null;
			case "date": {
				const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
				return m && isRealDate(+m[1], +m[2], +m[3]) ? v : null;
			}
			case "time":
				return /^([01]\d|2[0-3]):[0-5]\d$/.test(v) ? v : null;
			case "timestamp": {
				const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):[0-5]\d$/);
				return m && isRealDate(+m[1], +m[2], +m[3]) ? v : null;
			}
			case "enum.id":
				// must be one of the enum ids we offered for this attribute
				if (!attr.enums?.some((e) => e.id === v)) return null;
				return getLongId(v) || null;
			case "text generation":
			default:
				return v;
		}
	}

	async function askLLM(chatMessages: Message[], question: Question) {
		if (!graphStore || !domainStore || !configStore) init();

		let content: Content = {
			chat: [],
			// TODO not ideal to hardcode this to "de", but using languagePrefence is also not the ideal solution,
			// we have to come up with something more robust
			question_to_answer: question.title["de"] + " - " + question.subtitle["de"],
			attributes: [],
		};
		content.chat = chatMessages.map((msg) => {
			msg = msg as Message;
			// text domain lang
			if (msg.lang === domainStore.languagePreference) return msg.words.map((w) => w.value).join(" ");
			// translation domain lang
			else if (msg.translation && msg.translation[domainStore.languagePreference])
				return msg.translation[domainStore.languagePreference].value;
			// text engl
			else if (msg.lang === "en") return msg.words.map((w) => w.value).join(" ");
			// translation engl
			else if (msg.translation && msg.translation["en"]) return msg.translation["en"].value;
			// text any
			else return msg.words.map((w) => w.value).join(" ");
		});

		question.instanceTask.outputAttributes.forEach((instanceAttrId) => {
			let instanceAttr = graphStore.instance.attribute.byId(instanceAttrId);
			if (!instanceAttr) return;
			let conceptAttr = graphStore.concept.attribute.byId(instanceAttr.concept);
			if (!conceptAttr) return;

			let name = symbolsStore.term({ id: conceptAttr.refersTo, terminology: conceptAttr.terminology });
			let description = symbolsStore.description({
				id: conceptAttr.refersTo,
				terminology: conceptAttr.terminology,
			});
			if (!name && !description) return;

			let enums: ContentEnum[] = [];
			let arraytype = graphStore.descriptor.datatype.byId(conceptAttr.arraytype);
			let datatype = graphStore.descriptor.datatype.byId(conceptAttr.datatype);
			if (arraytype && arraytype.enumerationItems.length > 0) {
				enums =
					arraytype.enumerationItems.map((enumItemId) => {
						let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
						let synonyms = symbolsStore.synonyms({
							id: enumItem?.refersTo,
							terminology: enumItem?.terminology,
						});
						return {
							name: symbolsStore.term({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							description: symbolsStore.description({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							...(synonyms && synonyms.length > 0 && { synonyms }),
							id: getShortId(enumItemId),
						};
					}) || [];
			} else if (datatype && datatype.enumerationItems.length > 0) {
				enums =
					datatype.enumerationItems.map((enumItemId) => {
						let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
						let synonyms = symbolsStore.synonyms({
							id: enumItem?.refersTo,
							terminology: enumItem?.terminology,
						});
						return {
							name: symbolsStore.term({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							description: symbolsStore.description({
								id: enumItem?.refersTo,
								terminology: enumItem?.terminology,
							}),
							...(synonyms && synonyms.length > 0 && { synonyms }),
							id: getShortId(enumItemId),
						};
					}) || [];
			}

			// TODO re-evaluate if we should re-implement this - jnhck, 24.03.2026
			// if (enums.length > 10) {
			// 	enums = enums
			// 		.map((enumItem) => {
			// 			const nameScore = enumItem.name
			// 				.toLowerCase()
			// 				.split(" ")
			// 				.reduce(
			// 					(acc, word) => (content.chat.join(" ").toLowerCase().includes(word) ? acc + 1 : acc),
			// 					0,
			// 				);

			// 			const synonymsScore = enumItem.synonyms
			// 				?.map((synonym) =>
			// 					synonym
			// 						.toLowerCase()
			// 						.split(" ")
			// 						.reduce(
			// 							(acc, word) =>
			// 								content.chat.join(" ").toLowerCase().includes(word) ? acc + 1 : acc,
			// 							0,
			// 						),
			// 				)
			// 				.reduce((acc, score) => acc + score, 0);

			// 			return {
			// 				...enumItem,
			// 				score: nameScore + (synonymsScore || 0),
			// 			};
			// 		})
			// 		.sort((a, b) => b.score - a.score)
			// 		.slice(0, 10)
			// 		.map(({ score, ...rest }) => rest); // Remove the score property
			// }
			// END TEMPORARY

			// When the attribute is an array, its element type is described by arraytype (datatype.id === "array").
			const isArray = datatype?.id === "array";
			const elementType = isArray ? arraytype : datatype;

			let expectedResponse: ExpectedResponse;
			if (enums.length > 0) {
				expectedResponse = "enum.id";
			} else if (elementType && ["number", "date", "time", "timestamp"].includes(elementType.id)) {
				expectedResponse = elementType.id as ExpectedResponse;
			} else {
				expectedResponse = "text generation";
			}

			content.attributes.push({
				name,
				description,
				id: getShortId(instanceAttrId),
				expectedResponse,
				...(isArray && { isArray: true }),
				...(enums.length > 0 && { enums }),
			});
		});

		if (content.attributes.length === 0) return;

		const presentTypes = new Set(content.attributes.map((a) => a.expectedResponse));
		const hasArray = content.attributes.some((a) => a.isArray);
		let systemMessage = getSystemPrompt(presentTypes, hasArray);

		let userContent = JSON.stringify(content);

		let messages = [
			{
				role: "user",
				content: userContent,
			},
		];

		messages.unshift({
			role: "system",
			content: systemMessage,
		});

		currentlyProcessing.value += 1;
		api()
			.gpt()
			.post({
				model: configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || ""),
				messages: messages,
				internal_type: "suggestions (client side)",
				temperature: 1,
				jsonMode: false,
			})
			.then((r) => {
				let gptRes = r;
				gptRes = improveGptResponse(gptRes);
				try {
					// values may be a single value or, for array attributes, a list of values
					// example: { "ID1": "generated text", "ID2": "ID3", "ID3": "?", "ID4": ["ID5", "ID6"] }
					let answerJSON: { [key: string]: string | string[] } = JSON.parse(gptRes);
					if (typeof answerJSON !== "object" || answerJSON === null)
						throw new Error("GPT Answer is not an object");

					// map each returned id back to the attribute we asked about, so its value
					// can be validated against the expected datatype
					const attrById = new Map(content.attributes.map((a) => [a.id, a] as [string, ContentAttribute]));

					Object.entries(answerJSON).forEach(([key, val]) => {
						const attr = attrById.get(key);
						if (!attr) return; // ignore unknown / hallucinated ids
						const longKey = getLongId(key);
						if (!longKey) return;

						if (attr.isArray) {
							// array attributes must receive an array; validate each element, drop invalid ones
							if (!Array.isArray(val)) return;
							const cleaned = [
								...new Set(
									val
										.map((el) => validateAndNormalize(attr, el))
										.filter((el): el is string => el !== null),
								),
							];
							if (cleaned.length > 0) suggestionData.value[longKey] = cleaned;
						} else {
							// single-value attributes must not receive an array
							if (Array.isArray(val)) return;
							const cleaned = validateAndNormalize(attr, val);
							if (cleaned !== null) suggestionData.value[longKey] = cleaned;
						}
					});
				} catch (error) {
					console.error("Error parsing GPT response: ", error);
				}
			})
			.catch((err) => console.warn(err))
			.finally(() => (currentlyProcessing.value -= 1));
	}

	// New Alternis (server side)
	let eventSource: EventSource | null = null;
	function initSSE() {
		if (!alternisNewStore || !workspacesStore) init();
		if (!workspacesStore.openedWorkspaceId) return;
		closeSSE(); // close any existing connection to avoid duplicate entries on the server
		let endpoint = operatorAPIConfig.host;
		endpoint += "/sseSuggestions";
		const url = new URL(endpoint, window.location.href);
		url.searchParams.set("domainId", domainStore.domain.id);
		url.searchParams.set("workspaceId", workspacesStore.openedWorkspaceId);
		url.searchParams.set(
			"suggestionModel",
			configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || ""),
		);
		eventSource = new EventSource(url.toString());

		const currentWorkspaceId = workspacesStore.openedWorkspaceId; // workspaceId at the time of connection

		eventSource.onopen = (e) => {};
		eventSource.onmessage = (e) => {
			// Ignore messages from other workspace sse connections
			if (workspacesStore.openedWorkspaceId !== currentWorkspaceId) return;

			let data = JSON.parse(e.data) as Suggestions;
			for (const key in data) {
				suggestionData.value[key] = data[key];
			}
		};
		eventSource.onerror = (e) => {
			console.log("SSE error", e);
		};
	}
	function closeSSE() {
		if (eventSource) eventSource.close();
	}

	function getSystemPrompt(presentTypes: Set<ExpectedResponse>, hasArray: boolean) {
		const hasEnum = presentTypes.has("enum.id");
		const hasText = presentTypes.has("text generation");
		const hasNumber = presentTypes.has("number");
		const hasDate = presentTypes.has("date");
		const hasTime = presentTypes.has("time");
		const hasTimestamp = presentTypes.has("timestamp");

		const valueDescParts: string[] = [];
		if (hasEnum) valueDescParts.push("enum.id");
		if (hasText) valueDescParts.push("text");
		if (hasNumber) valueDescParts.push("number");
		if (hasDate) valueDescParts.push("date");
		if (hasTime) valueDescParts.push("time");
		if (hasTimestamp) valueDescParts.push("timestamp");
		const valueDesc = valueDescParts.join(" or ");

		let prompt = `You are a highly reliable JSON extraction system designed for critical IT applications where accuracy is paramount.

# Two-Step Decision Process
## Step 1: Confidence Assessment
First, assess if you can confidently extract information for each attribute based on explicit evidence in the provided chat. For each attribute, ask:
- Is there clear, unambiguous evidence in the chat to determine this value?
- Would another qualified analyst looking at the same data reach the same conclusion?
- Would you stake your reliability on this extraction being correct?

Only proceed with extraction for attributes where you can answer "yes" to all questions above.

## Step 2: Precise Extraction
For attributes that pass Step 1, follow these strict extraction rules:

# Task
Extract specific <attributes> from a given <chat> based solely on explicit evidence. You will analyze:
- A list of <attributes> (JSON objects with fields: name, id, description, expectedResponse${
			hasArray ? ", optionally isArray" : ""
		}${hasEnum ? ", and optionally enums" : ""})
- A <chat> (message history as a string array)
- A <question_to_answer> (provides additional context)

# Response Rules
`;

		let ruleNum = 1;
		if (hasEnum) {
			prompt += `${ruleNum}. For enum attributes (expectedResponse = "enum.id"):
   - Return ONLY a valid enum.id from the provided list
   - Never return enum.name or invent values
   - If uncertain which enum.id applies, exclude the attribute entirely

`;
			ruleNum++;
		}
		if (hasText) {
			prompt += `${ruleNum}. For text generation (expectedResponse = "text generation"):
   - Generate concise, factual text based solely on chat content

`;
			ruleNum++;
		}
		if (hasNumber) {
			prompt += `${ruleNum}. For number attributes (expectedResponse = "number"):
   - Return ONLY a single numeric value (e.g. "3", "99.50"), never a range or text
   - Do not return vague quantities like "a few", "several", "many" — if no concrete number or range is stated, exclude the attribute
   - If only a range is given (e.g. "between 70 and 80"), return the single value within that range that is the safest, most cautious assumption for this specific attribute — the value least likely to cause harm if acted upon. Which bound is "safest" depends on what the attribute means (e.g. for a casualty count return the higher bound, for remaining time return the lower bound). For a plain approximation (e.g. "around 75"), return the stated value
   - Avoid digit group separators (e.g. use "1000" not "1,000" or "1.000"), use points for decimals (e.g. "99.50")

`;
			ruleNum++;
		}
		if (hasDate) {
			prompt += `${ruleNum}. For date attributes (expectedResponse = "date"):
   - Return the date in "YYYY-MM-DD" format
   - Resolve relative references (e.g. "yesterday", "last Monday") using the current date provided below

`;
			ruleNum++;
		}
		if (hasTime) {
			prompt += `${ruleNum}. For time attributes (expectedResponse = "time"):
   - Return the time in "HH:MM" format (24-hour)

`;
			ruleNum++;
		}
		if (hasTimestamp) {
			prompt += `${ruleNum}. For timestamp attributes (expectedResponse = "timestamp"):
   - Return the timestamp in "YYYY-MM-DDTHH:MM" format
   - Resolve relative references using the current date and time provided below

`;
			ruleNum++;
		}
		if (hasArray) {
			prompt += `${ruleNum}. For array attributes (isArray = true):
   - Return a JSON array of values, each value following the rule for its expectedResponse
   - Include every distinct value explicitly supported by the chat; do not repeat duplicates
   - If no value can be confidently determined, exclude the attribute entirely (do not return an empty array)

`;
			ruleNum++;
		}

		prompt += `# Critical Exclusion Requirements
- Exclude an attribute if there is any uncertainty about its value
- Never guess, assume, or infer beyond what is explicitly stated
- When in doubt, omit the attribute from your response
`;
		if (hasEnum) {
			prompt += `- For enum attributes, if you cannot determine with certainty which enum.id applies, exclude it
`;
		}
		if (hasNumber) {
			prompt += `- For number attributes, if no concrete number is explicitly stated, exclude the attribute
`;
		}

		prompt += `
# Response Format
Return a valid JSON object with this structure:
\`\`\`json
{
  "<attribute.id>": "<${valueDesc}>"
}
\`\`\`
- Include ONLY attributes you can determine with high confidence
- An empty JSON object {} is a valid response if no attributes can be confidently determined
- Output only the JSON without explanations, headers, or commentary${
			hasArray
				? `\n- For attributes with isArray = true, the value must be a JSON array: "<attribute.id>": ["<${valueDesc}>", ...]`
				: ""
		}
- To handle relative dates correctly, you need to know the current date and time: ${getLocalISOString()}
`;
		// Example section — one coherent scenario. For each present type we include one attribute
		// whose value can be inferred from the dialogue and one that must be excluded, so the model
		// sees both a correct extraction and a correct omission for every datatype it will encounter.
		const today = getLocalISOString().slice(0, 10); // YYYY-MM-DD, lets the example resolve "today"

		const exampleChat = [
			"Emergency line, what happened?",
			"There's been a car accident on Hauptstraße. One person is injured and trapped in the vehicle.",
			"Is the injured person conscious?",
			"No, they are unconscious. They have injuries to the head and the left arm.",
		];
		if (hasNumber) {
			exampleChat.push("How old is the injured person?", "Hard to say exactly, somewhere between 70 and 80.");
		}
		exampleChat.push("When did this happen?", "It happened today at around 14:30.");
		const exampleQuestion = "What are the key details of the accident and the condition of the injured person?";

		const exampleAttrs: string[] = [];
		const exampleResponse: Record<string, string | string[]> = {};
		const exampleNotes: string[] = [];

		if (hasText) {
			exampleAttrs.push(
				`{"name": "streetname", "id": "ID767", "description": "", "expectedResponse": "text generation"}`,
			);
			exampleResponse["ID767"] = "Hauptstraße";
			exampleAttrs.push(
				`{"name": "name of injured person", "id": "ID768", "description": "", "expectedResponse": "text generation"}`,
			);
			exampleNotes.push(
				`The "name of injured person" was excluded because the dialogue never states the person's name.`,
			);
		}
		if (hasNumber) {
			exampleAttrs.push(
				`{"name": "number of injured persons", "id": "ID112", "description": "", "expectedResponse": "number"}`,
			);
			exampleResponse["ID112"] = "1";
			exampleAttrs.push(
				`{"name": "age of injured person", "id": "ID114", "description": "", "expectedResponse": "number"}`,
			);
			exampleResponse["ID114"] = "80";
			exampleAttrs.push(
				`{"name": "vehicle speed", "id": "ID113", "description": "", "expectedResponse": "number"}`,
			);
			exampleNotes.push(
				`The "age of injured person" was given only as the range "between 70 and 80"; the higher bound (80) is returned because assuming the patient is older is the more cautious medical assumption. The "vehicle speed" was excluded because no concrete number is stated.`,
			);
		}
		if (hasDate) {
			exampleAttrs.push(
				`{"name": "date of accident", "id": "ID890", "description": "", "expectedResponse": "date"}`,
			);
			exampleResponse["ID890"] = today;
			exampleAttrs.push(
				`{"name": "date of birth", "id": "ID893", "description": "", "expectedResponse": "date"}`,
			);
			exampleNotes.push(
				`The "date of accident" was inferred by resolving "today" against the provided current date. The "date of birth" was excluded because it is never mentioned.`,
			);
		}
		if (hasTime) {
			exampleAttrs.push(
				`{"name": "time of accident", "id": "ID891", "description": "", "expectedResponse": "time"}`,
			);
			exampleResponse["ID891"] = "14:30";
			exampleAttrs.push(
				`{"name": "arrival time of emergency services", "id": "ID894", "description": "", "expectedResponse": "time"}`,
			);
			exampleNotes.push(`The "arrival time of emergency services" was excluded because it is never mentioned.`);
		}
		if (hasTimestamp) {
			exampleAttrs.push(
				`{"name": "accident timestamp", "id": "ID892", "description": "", "expectedResponse": "timestamp"}`,
			);
			exampleResponse["ID892"] = `${today}T14:30`;
			exampleAttrs.push(
				`{"name": "handover timestamp", "id": "ID895", "description": "point in time at which responsibility for the patient is transferred to another party", "expectedResponse": "timestamp"}`,
			);
			exampleNotes.push(`The "handover timestamp" was excluded because it is never mentioned.`);
		}
		if (hasEnum) {
			exampleAttrs.push(
				`{"name": "consciousness state", "id": "ID469", "description": "", "expectedResponse": "enum.id", "enums": [{"name": "conscious", "id": "ID389"}, {"name": "unconscious", "id": "ID324"}]}`,
			);
			exampleResponse["ID469"] = "ID324";
			exampleAttrs.push(
				`{"name": "gender", "id": "ID431", "description": "", "expectedResponse": "enum.id", "enums": [{"name": "male", "id": "ID613"}, {"name": "female", "id": "ID14"}]}`,
			);
			exampleNotes.push(
				`The "gender" was excluded because the pronoun "they" does not reveal the gender, so there is insufficient explicit evidence.`,
			);
		}
		if (hasArray) {
			if (hasEnum) {
				exampleAttrs.push(
					`{"name": "injured body regions", "id": "ID950", "description": "", "expectedResponse": "enum.id", "isArray": true, "enums": [{"name": "head", "id": "ID951"}, {"name": "left arm", "id": "ID952"}, {"name": "chest", "id": "ID953"}]}`,
				);
				exampleResponse["ID950"] = ["ID951", "ID952"];
				exampleNotes.push(
					`The "injured body regions" is an array, so every distinct value mentioned ("head" and "left arm") is returned together; "chest" is omitted because it is not mentioned.`,
				);
			} else {
				exampleAttrs.push(
					`{"name": "injured body regions", "id": "ID950", "description": "", "expectedResponse": "text generation", "isArray": true}`,
				);
				exampleResponse["ID950"] = ["head", "left arm"];
				exampleNotes.push(
					`The "injured body regions" is an array, so every distinct value mentioned ("head" and "left arm") is returned together.`,
				);
			}
		}

		if (exampleAttrs.length > 0) {
			prompt += `
# Example
## Input
{
  "chat": ${JSON.stringify(exampleChat)},
  "question_to_answer": ${JSON.stringify(exampleQuestion)},
  "attributes": [
    ${exampleAttrs.join(",\n    ")}
  ]
}

## Correct Response
${JSON.stringify(exampleResponse, null, 2)}
`;
			if (exampleNotes.length > 0) {
				prompt += exampleNotes.map((n) => `Note: ${n}`).join(`\n`) + `\n`;
			}
		}

		return prompt;
	}

	// Reconnect SSE when the suggestion model changes
	const currentModel = computed(() => {
		if (!configStore || !domainStore || !dataspaceStore) return null;
		return configStore.getSuggestionModel(domainStore.domain.id, dataspaceStore.openedDataspaceId || "");
	});
	watch(currentModel, () => {
		if (eventSource) initSSE();
	});

	return {
		suggestionData,
		askLLM,
		currentlyProcessing,
		initSSE,
		closeSSE,
	};
});
